import { useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import PoweredByFooter from "@/components/PoweredByFooter";

/* Perfect A4 landscape "Bill Report" — one row per patient with sub-rows
   per bill-item (Consultation + each X-Ray / Lab / Package line), showing
   doctor as the remark and the per-patient Bill / Discount / Net / Recd
   on the last row of the group. Walk-in investigation test names are
   expanded so the owner can see WHICH test the patient paid for. */

const fmtD = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
};

export default function ReportPrint() {
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const brand = useBranding();

  useEffect(() => {
    const params = {};
    for (const [k, v] of sp.entries()) params[k] = v;
    api.get("/reports/full", { params }).then((r) => setData(r.data));
  }, [sp]);
  useEffect(() => {
    if (data) {
      window.onafterprint = () => window.close();
      setTimeout(() => window.print(), 500);
    }
  }, [data]);
  useEffect(() => {
    document.documentElement.classList.add("print-mode-landscape");
    return () => document.documentElement.classList.remove("print-mode-landscape");
  }, []);

  if (!data) return <div className="p-8">Loading bill report…</div>;

  const t = data.totals;
  const from = data.range.from, to = data.range.to;
  const sameDay = from === to;

  // Group bill_items by patient_id. For each patient, list each line in a
  // deterministic order (Consultation first, then investigations, then
  // other charges). Each line keeps its OPD/IPD id so we can pull the
  // bill number + payment mode from the parent OPD.
  const opdById = Object.fromEntries((data.opds || []).map((o) => [o.id, o]));
  const ipdById = Object.fromEntries((data.ipds || []).map((i) => [i.id, i]));
  const patById = Object.fromEntries((data.patient_wise || []).map((p) => [p.patient_id, p]));

  // Deduplicate / normalise line description — strip the "· from Walk-in …"
  // suffix added by IPD import so the report shows the raw test name.
  const cleanDesc = (d) => (d || "").split("·")[0].trim();

  // Assemble groups
  const groups = {};
  for (const it of data.bill_items || []) {
    if (it.is_void) continue;
    const pid = it.patient_id;
    if (!pid) continue;
    const g = groups[pid] = groups[pid] || { patient_id: pid, lines: [] };
    const parent = opdById[it.opd_id] || ipdById[it.ipd_id] || {};
    g.lines.push({
      id: it.id,
      date: it.date || parent.created_at || parent.admission_date,
      bill_no: parent.opd_no || parent.ipd_no || "—",
      mode: parent.payment_mode || it.mode || "Cash",
      scheme: parent.payment_scheme_name,
      status: parent.payment_status,
      description: cleanDesc(it.description),
      doctor: it.doctor_name || parent.doctor_name || parent.reference_doctor || "",
      qty: it.quantity || 1,
      rate: it.rate || 0,
      amount: it.amount || 0,
    });
  }

  // Also surface patients who have an OPD/consultation but no explicit
  // Consultation bill_item (defensive — some flows store fee only on the
  // OPD doc). Walk-in investigations with 0 bill_items also get surfaced
  // through this path.
  for (const o of data.opds || []) {
    if (!o.patient_id) continue;
    const pid = o.patient_id;
    const g = groups[pid] = groups[pid] || { patient_id: pid, lines: [] };
    const haveConsult = g.lines.some((l) => l.bill_no === o.opd_no && /consult/i.test(l.description));
    const fee = Number(o.consultation_fee || 0);
    const isWalkin = o.is_walkin || o.department === "Walk-in Investigation";
    if (!haveConsult && fee > 0 && !isWalkin) {
      g.lines.unshift({
        id: `cf-${o.id}`,
        date: o.created_at,
        bill_no: o.opd_no,
        mode: o.payment_mode || "Cash",
        description: "Consultation Charges",
        doctor: o.doctor_name,
        qty: 1,
        rate: fee,
        amount: fee,
      });
    }
    // For walk-ins, if the only bill_items are already present we trust them;
    // if there are none at all, synthesise rows from the OPD's investigations array.
    if (isWalkin && g.lines.filter((l) => l.bill_no === o.opd_no).length === 0 && (o.investigations || []).length > 0) {
      const share = Number(o.investigations_total || 0) / (o.investigations || []).length;
      (o.investigations || []).forEach((name, idx) => g.lines.push({
        id: `winv-${o.id}-${idx}`,
        date: o.created_at,
        bill_no: o.opd_no,
        mode: o.payment_mode || "Cash",
        scheme: o.payment_scheme_name,
        status: o.payment_status,
        description: name,
        doctor: o.reference_doctor || "",
        qty: 1,
        rate: share,
        amount: share,
      }));
    }
  }

  // Sort lines inside each group — consultation first, then by date, then description
  for (const g of Object.values(groups)) {
    g.lines.sort((a, b) => {
      const consultA = /consult/i.test(a.description) ? 0 : 1;
      const consultB = /consult/i.test(b.description) ? 0 : 1;
      if (consultA !== consultB) return consultA - consultB;
      return String(a.date).localeCompare(String(b.date));
    });
  }

  // Attach patient facts + totals
  const rows = Object.values(groups).map((g) => {
    const pw = patById[g.patient_id] || {};
    const bill = g.lines.reduce((s, l) => s + Number(l.amount || 0), 0);
    const discount = Number(pw.discount || 0);
    const received = (pw.modes?.CASH || 0) + (pw.modes?.UPI || 0) + (pw.modes?.CARD || 0) + (pw.modes?.PMJAY || 0) + (pw.modes?.PACKAGE || 0);
    return {
      patient_id: g.patient_id,
      uhid: pw.uhid || "",
      name: pw.name || "",
      lines: g.lines,
      bill, discount,
      net: bill - discount,
      received,
      credit_outstanding: pw.modes?.CREDIT || 0,
    };
  }).sort((a, b) => (b.bill - b.discount) - (a.bill - a.discount));

  // Grand totals
  const G = rows.reduce((a, r) => ({
    bill: a.bill + r.bill,
    discount: a.discount + r.discount,
    net: a.net + r.net,
    received: a.received + r.received,
    credit: a.credit + r.credit_outstanding,
  }), { bill: 0, discount: 0, net: 0, received: 0, credit: 0 });

  // Payment-mode roll-up
  const modeTotals = { CASH: 0, UPI: 0, PMJAY: 0, PACKAGE: 0, CARD: 0, CREDIT: 0 };
  for (const p of data.patient_wise || []) {
    for (const k of Object.keys(modeTotals)) modeTotals[k] += Number(p.modes?.[k] || 0);
  }

  return (
    <>
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 6mm 6mm 8mm 6mm; }
          .avoid-break, tr { break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          .no-print { display: none !important; }
          html, body { background: white; margin: 0; }
          .pageframe { box-shadow: none !important; margin: 0 !important; padding: 0 !important; width: 100% !important; max-width: 285mm !important; }
          .page-break { break-after: page; }
        }
        .bill-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .bill-table th, .bill-table td { border: 1px solid #334155; padding: 2px 4px; vertical-align: top; font-size: 9.5px; line-height: 1.22; word-wrap: break-word; overflow-wrap: anywhere; }
        .bill-table th { background: #e2e8f0; font-weight: 600; text-align: center; letter-spacing: 0.03em; font-size: 9px; text-transform: uppercase; }
        .bill-table td.num { text-align: right; font-family: ui-monospace, "SF Mono", Menlo, monospace; }
        .bill-table td.ctr { text-align: center; }
        .bill-table tbody tr.pgroup td { background: #f8fafc; }
        .bill-table tfoot td { background: #cbd5e1; font-weight: 700; font-size: 10px; }
        .mono { font-family: ui-monospace, "SF Mono", Menlo, monospace; }
        .mode-pill { display:inline-block; padding: 0 3px; border-radius: 2px; font-size: 8.5px; letter-spacing: 0.03em; }
        .mode-CASH    { background:#ccfbf1; color:#065f46; }
        .mode-UPI     { background:#dbeafe; color:#1e40af; }
        .mode-PMJAY   { background:#ede9fe; color:#6b21a8; }
        .mode-PACKAGE { background:#ede9fe; color:#6b21a8; }
        .mode-CARD    { background:#f1f5f9; color:#334155; }
        .mode-CREDIT  { background:#fee2e2; color:#991b1b; }
        .vert-table th, .vert-table td { padding: 3px 6px; font-size: 10px; }
      `}</style>

      <div className="min-h-screen bg-slate-100 flex justify-center py-6">
        <div className="pageframe bg-white shadow-lg px-5 py-4" style={{ width: "285mm" }}>
          {/* Header */}
          <div className="text-center border-b-2 border-slate-800 pb-2">
            <div className="font-display font-bold text-xl tracking-wide uppercase">{brand.name}</div>
            <div className="text-[10px] mt-0.5 text-slate-600">
              {[brand.address, brand.city, brand.state].filter(Boolean).join(", ")}
              {brand.phone && <> · {brand.phone}</>}
            </div>
            <div className="font-semibold text-sm mt-1">Bill Report</div>
            <div className="text-[10px] mt-0.5 text-slate-700">
              From : <b>{fmtD(from)}</b>
              {!sameDay && <> &nbsp;To &nbsp;<b>{fmtD(to)}</b></>}
              &nbsp;&nbsp;·&nbsp;&nbsp; Print Date : <b>{fmtD(new Date().toISOString())}</b>
            </div>
          </div>

          {/* Headline KPI strip (compact) */}
          <div className="mt-2 flex items-center justify-between text-[10px] bg-slate-50 border border-slate-300 px-3 py-1.5">
            <span>OPD <b>{t.opd_count}</b></span>
            <span>Walk-in <b>{t.walkin_count}</b></span>
            <span>IPD <b>{t.ipd_count}</b></span>
            <span>Emergency <b>{t.emergency_count}</b></span>
            <span>Collection <b className="font-mono">{fmtINR(t.collection)}</b></span>
            <span>Advance <b className="font-mono">{fmtINR(t.advance)}</b></span>
            <span>Discount <b className="font-mono">{fmtINR(t.discount_given || 0)}</b></span>
            <span className="text-red-700">Credit Due <b className="font-mono">{fmtINR(t.credit_outstanding || 0)}</b></span>
          </div>

          {/* Main Bill Report table */}
          <table className="bill-table mt-3">
            <colgroup>
              <col style={{ width: "7%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "27%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Reg. No.</th>
                <th>Patient Name</th>
                <th>Date</th>
                <th>Pay Mode</th>
                <th>Bill No.</th>
                <th>Remarks (Service · Doctor)</th>
                <th>Bill Amount</th>
                <th>Disc. Amount</th>
                <th>Net Amount</th>
                <th>Recd Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const n = r.lines.length || 1;
                const showPatientTotal = n > 1;
                return (
                  <Fragment key={r.patient_id}>
                    {r.lines.map((l, idx) => (
                      <tr key={l.id} className={idx === 0 ? "pgroup" : ""}>
                        {idx === 0 && <td className="ctr" rowSpan={n}><span className="mono">{r.uhid}</span></td>}
                        {idx === 0 && <td rowSpan={n}><b>{r.name}</b></td>}
                        <td className="ctr">{fmtD(l.date)}</td>
                        <td className="ctr">
                          <span className={`mode-pill mode-${(l.mode || "CASH").toUpperCase()}`}>{l.mode}</span>
                          {l.scheme && <div className="text-[9px] text-slate-600 mt-0.5">{l.scheme}</div>}
                        </td>
                        <td className="ctr mono">{l.bill_no}</td>
                        <td>
                          <div className="flex items-start justify-between gap-2">
                            <span>{l.description}</span>
                            {l.doctor && <span className="text-[9px] text-slate-600 whitespace-nowrap">{l.doctor}</span>}
                          </div>
                          <div className="text-[9px] text-slate-500 mt-0.5 font-mono">
                            {l.qty > 1 && <>{l.qty} × </>}{fmtINR(l.rate)}
                          </div>
                        </td>
                        <td className="num">{fmtINR(l.amount)}</td>
                        {idx === n - 1 && !showPatientTotal ? (
                          <>
                            <td className="num">{r.discount ? fmtINR(r.discount) : "0"}</td>
                            <td className="num"><b>{fmtINR(r.net)}</b></td>
                            <td className="num">{fmtINR(r.received)}</td>
                          </>
                        ) : (
                          <><td></td><td></td><td></td></>
                        )}
                      </tr>
                    ))}
                    {showPatientTotal && (
                      <tr key={`${r.patient_id}-total`} className="patient-total-row">
                        <td colSpan={6} className="num" style={{ fontWeight: 700, background: "#fef3c7" }}>
                          Patient Total — {r.name}
                        </td>
                        <td className="num" style={{ fontWeight: 700, background: "#fef3c7" }}>{fmtINR(r.bill)}</td>
                        <td className="num" style={{ fontWeight: 700, background: "#fef3c7" }}>{fmtINR(r.discount)}</td>
                        <td className="num" style={{ fontWeight: 700, background: "#fef3c7" }}>{fmtINR(r.net)}</td>
                        <td className="num" style={{ fontWeight: 700, background: "#fef3c7" }}>{fmtINR(r.received)}</td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="ctr p-4 text-slate-500">No bills in the selected range</td></tr>
              )}
              {rows.length > 0 && (
                <tr className="grand-total-row" data-testid="bill-report-grand-total">
                  <td colSpan={6} className="num" style={{ fontWeight: 700, background: "#cbd5e1" }}>GRAND TOTAL</td>
                  <td className="num" style={{ fontWeight: 700, background: "#cbd5e1" }}>{fmtINR(G.bill)}</td>
                  <td className="num" style={{ fontWeight: 700, background: "#cbd5e1" }}>{fmtINR(G.discount)}</td>
                  <td className="num" style={{ fontWeight: 700, background: "#cbd5e1" }}>{fmtINR(G.net)}</td>
                  <td className="num" style={{ fontWeight: 700, background: "#cbd5e1" }}>{fmtINR(G.received)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Payment-mode summary — VERTICAL list */}
          <div className="mt-4 avoid-break">
            <div className="font-semibold text-xs uppercase tracking-wider border-b pb-1 mb-2">Payment-mode Summary</div>
            <table className="bill-table vert-table" style={{ maxWidth: "80mm" }}>
              <thead>
                <tr><th>Mode</th><th>Amount</th><th>Share</th></tr>
              </thead>
              <tbody>
                {(() => {
                  const total = Object.values(modeTotals).reduce((a, b) => a + b, 0) || 1;
                  const order = [
                    ["Cash", "CASH"], ["UPI / GPay", "UPI"], ["Card", "CARD"],
                    ["PMJAY", "PMJAY"], ["Package", "PACKAGE"], ["Credit (Outstanding)", "CREDIT"],
                  ];
                  return order.map(([lbl, k]) => (
                    <tr key={k}>
                      <td>{lbl}</td>
                      <td className="num">{fmtINR(modeTotals[k])}</td>
                      <td className="num">{((modeTotals[k] * 100) / total).toFixed(1)}%</td>
                    </tr>
                  ));
                })()}
              </tbody>
              <tfoot>
                <tr>
                  <td>TOTAL</td>
                  <td className="num">{fmtINR(Object.values(modeTotals).reduce((a, b) => a + b, 0))}</td>
                  <td className="num">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Investigation breakdown — VERTICAL list */}
          {(data.investigation_by_type || []).length > 0 && (
            <div className="mt-4 avoid-break">
              <div className="font-semibold text-xs uppercase tracking-wider border-b pb-1 mb-2">Investigation Breakdown (by Type)</div>
              <table className="bill-table vert-table" style={{ maxWidth: "80mm" }}>
                <thead><tr><th>Type</th><th>Tests</th><th>Amount</th></tr></thead>
                <tbody>
                  {(data.investigation_by_type || []).map((row) => (
                    <tr key={row.type}>
                      <td>{row.type}</td>
                      <td className="num">{row.count}</td>
                      <td className="num">{fmtINR(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>TOTAL</td>
                    <td className="num">{(data.investigation_by_type || []).reduce((s, x) => s + x.count, 0)}</td>
                    <td className="num">{fmtINR((data.investigation_by_type || []).reduce((s, x) => s + x.amount, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Voided audit section intentionally omitted from the printed
              report — voided rows should not clutter the owner's daily
              record. Full audit remains available on-screen under
              /reports and the audit_logs collection. */}

          {/* Expenses & Refunds (if any — voided ones are intentionally skipped so they don't clutter the owner's report) */}
          {((data.expenses || []).length + (data.refunds || []).filter((r) => !r.is_void).length) > 0 && (
            <div className="mt-4 avoid-break page-break">
              <div className="font-semibold text-xs uppercase tracking-wider border-b pb-1 mb-2">Expenses &amp; Refunds</div>
              <table className="bill-table">
                <thead>
                  <tr><th>Type</th><th>Date</th><th>Description</th><th>Category / Mode</th><th>Entered By</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  {(data.expenses || []).map((e) => (
                    <tr key={e.id}>
                      <td>Expense</td>
                      <td className="ctr">{fmtD(e.expense_date)}</td>
                      <td>{e.description}{e.remarks ? ` · ${e.remarks}` : ""}</td>
                      <td className="text-[10px]">{e.category} · {e.paid_mode}</td>
                      <td className="text-[10px]">{e.created_by_name || "—"}</td>
                      <td className="num">− {fmtINR(e.amount)}</td>
                    </tr>
                  ))}
                  {(data.refunds || []).filter((r) => !r.is_void).map((r) => (
                    <tr key={r.id}>
                      <td>Refund</td>
                      <td className="ctr">{fmtD(r.created_at)}</td>
                      <td>{r.patient_name || "—"}{r.notes ? ` · ${r.notes}` : ""}</td>
                      <td className="text-[10px]">— · {r.mode || "Cash"}</td>
                      <td className="text-[10px]">{r.collected_by_name || "—"}</td>
                      <td className="num">− {fmtINR(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="num">SUBTOTAL (Expenses + Refunds)</td>
                    <td className="num">
                      − {fmtINR((data.expenses_total || 0) + (data.refunds || []).filter((r) => !r.is_void).reduce((s, r) => s + Number(r.amount || 0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Service-wise summary — VERTICAL list (Divine style) */}
          {(data.service_wise || []).length > 0 && (
            <div className="mt-4 avoid-break">
              <div className="font-semibold text-xs uppercase tracking-wider border-b pb-1 mb-2">
                Service-wise Summary (No of Cases × Total)
              </div>
              <table className="bill-table vert-table" style={{ maxWidth: "95mm" }}>
                <thead><tr><th>Service</th><th>Cases</th><th>Total</th></tr></thead>
                <tbody>
                  {(data.service_wise || []).map((row, idx) => (
                    <tr key={row.service}>
                      <td>{idx + 1}. {row.service}</td>
                      <td className="num">{row.count}</td>
                      <td className="num">{fmtINR(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>TOTAL</td>
                    <td className="num">{(data.service_wise || []).reduce((s, x) => s + x.count, 0)}</td>
                    <td className="num">{fmtINR((data.service_wise || []).reduce((s, x) => s + x.amount, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Grand Totals block — Divine-style end-of-day card */}
          <div className="mt-5 avoid-break">
            <div className="font-semibold text-xs uppercase tracking-wider border-b pb-1 mb-2">End-of-Day Grand Totals</div>
            {(() => {
              const bills = G.bill;
              const disc = G.discount;
              const net = G.net;
              const recd = G.received;
              const exp = data.expenses_total || 0;
              const refund = (data.refunds || []).filter((r) => !r.is_void).reduce((s, r) => s + Number(r.amount || 0), 0);
              const final = recd - exp - refund;
              return (
                <div className="grid grid-cols-2 gap-4">
                  <table className="bill-table">
                    <thead>
                      <tr><th>Payment Mode</th><th>Actual Amount</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Cash</td><td className="num">{fmtINR(modeTotals.CASH)}</td></tr>
                      <tr><td>UPI / GPay</td><td className="num">{fmtINR(modeTotals.UPI)}</td></tr>
                      <tr><td>Card</td><td className="num">{fmtINR(modeTotals.CARD)}</td></tr>
                      <tr><td>Package</td><td className="num">{fmtINR(modeTotals.PACKAGE)}</td></tr>
                      <tr><td>PM Jay</td><td className="num">{fmtINR(modeTotals.PMJAY)}</td></tr>
                      <tr><td>Credit (Outstanding)</td><td className="num">{fmtINR(modeTotals.CREDIT)}</td></tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td className="num"><b>{fmtINR(Object.values(modeTotals).reduce((a, b) => a + b, 0))}</b></td>
                      </tr>
                    </tfoot>
                  </table>

                  <table className="bill-table">
                    <tbody>
                      <tr><td><b>No. of Patients</b></td><td className="num"><b>{data.no_of_patients || 0}</b></td></tr>
                      <tr><td><b>No. of Bills</b></td><td className="num"><b>{data.no_of_bills || 0}</b></td></tr>
                      <tr><td>Bill Amount</td><td className="num">{fmtINR(bills)}</td></tr>
                      <tr><td>Discount Given</td><td className="num">− {fmtINR(disc)}</td></tr>
                      <tr><td><b>Net Billed</b></td><td className="num"><b>{fmtINR(net)}</b></td></tr>
                      <tr><td>Received</td><td className="num">{fmtINR(recd)}</td></tr>
                      <tr><td>Less: Expenses</td><td className="num">− {fmtINR(exp)}</td></tr>
                      <tr><td>Less: Refunds</td><td className="num">− {fmtINR(refund)}</td></tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><b>On-Hand Cash</b></td>
                        <td className="num" style={{ fontSize: "12px" }}><b>{fmtINR(final)}</b></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })()}
          </div>

          <div className="mt-6 border-t pt-2 text-[9px] text-center text-slate-500">
            {brand.name} · {brand.address} · Confidential — for internal accounts &amp; audit only
          </div>
          <PoweredByFooter />
        </div>

        <div className="fixed top-2 right-2 no-print">
          <button onClick={() => window.print()} className="bg-blue-700 text-white px-3 py-1 text-sm rounded-sm">Print</button>
        </div>
      </div>
    </>
  );
}
